import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Button, Flashbar, Modal, Box, SpaceBetween, Spinner, AppLayout, Alert } from '@amzn/awsui-components-core';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextProvider';
import { useEventsApiContext } from '../../context/EventsApiContextProvider';
import './EAHuddleHomePage.scss';
import { LeaderEAConfigService } from './services/LeaderEAConfigService';
import { UserEAConfigService } from './services/UserEAConfigService';

// Hooks
import { useAttendeeManagement } from './hooks/useAttendeeManagement';
import { useDocumentProcessing } from './hooks/useDocumentProcessing';
import { useFlashMessages } from './hooks/useFlashMessages';
import { useRecentMeetings } from './hooks/useRecentMeetings';
import { useScheduling } from './hooks/useScheduling';
import { useSharedMeeting } from './hooks/useSharedMeeting';
import { useMeetingPersistence } from './hooks/useMeetingPersistence';
import { useNotifications } from './hooks/useNotifications';
import { useUserEAConfig } from './hooks/useUserEAConfig';

// Containers
import { MeetingFormContainer } from './containers/MeetingFormContainer';
import { AddAttendeesContainer } from './containers/AddAttendeesContainer';
import { AttendeePathPicker, AttendeePath } from './containers/AttendeePathPicker';
import { UploadTemplateContainer } from './containers/modals/UploadTemplateModal';
import { ImportFromMeetingModal } from './containers/modals/ImportFromMeetingModal';
import { SchedulingContainer } from './containers/scheduling/SchedulingContainer';
import { TimezoneConverterContainer } from './containers/TimezoneConverterContainer';
// Components
import { Sidebar } from './components/Sidebar/Sidebar';
import { HyperBadge } from '../../generic-components/hyper-badge/HyperBadge';
import { EmployeeService } from './services/EmployeeService';

// Types & utils
import { Employee, AttendeePriority, RepeatOption, TimeSlot, CloneModalTab } from './interfaces/interface';
import { MEETING_CONSTANTS } from './constants/EAHuddleConstants';
import { DateTime } from 'luxon';

import { exportAttendeeHierarchyToCsv } from './utils/csvExportUtils';
import { generateMultiBadgerUrl, loadAttendeesInBatches, computeAuthorizedLogins, buildMeetingAttendees } from './utils/attendeeUtils';
import { EmployeeSettingsService } from './services/EmployeeSettingsService';
import { MeetingService } from './services/MeetingService';
import { EAHuddleService } from 'src/services/EAHuddleService';
import { BrassService, BrassGroupType } from 'src/services/BrassService';
import { v4 as uuidv4 } from 'uuid';
import { logger } from 'src/logger';
import { useImpersonation } from '../../context/ImpersonationContext';

export const EAHuddleHomePage: React.FC = () => {
  const { Alias: authAlias, DisplayName: authDisplayName } = useAuth();
  const { impersonation } = useImpersonation();
  const Alias = impersonation?.alias || authAlias;
  const DisplayName = impersonation?.displayName || authDisplayName;
  const nameParts = DisplayName?.split(',') || [];
  const firstName = nameParts.length >= 2 ? nameParts[1]?.trim() : DisplayName?.split(' ')[0];
  const fullName = nameParts.length >= 2 ? `${nameParts[1]?.trim()} ${nameParts[0]?.trim()}` : DisplayName;
  const eventsApiContext = useEventsApiContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── UI state ─────────────────────────────────────────────────────────
  const [showOutlookAlert, setShowOutlookAlert] = useState(() => localStorage.getItem('ea-huddle-outlook-alert-dismissed') !== 'true');
  const [showUploadTemplateModal, setShowUploadTemplateModal] = useState(false);
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(true);

  // ── Attachment state ─────────────────────────────────────────────────
  const [uploadedAttachments, setUploadedAttachments] = useState<Array<{ fileName: string; s3Uri: string }>>([]);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [proposedSlot, setProposedSlot] = useState<{ date: string; startTime: string; endTime: string; timezone: string } | null>(null);
  const [hoveredSlotId, setHoveredSlotId] = useState<string | null>(null);
  const [populateSlot, setPopulateSlot] = useState<{ date: string; startTime: string; endTime: string; timezone: string } | null>(null);
  const [directAddSlot, setDirectAddSlot] = useState<{ date: string; startTime: string; endTime: string; timezone: string } | null>(null);

  // ── Attendee path picker state ───────────────────────────────────────
  const [attendeePathChosen, setAttendeePathChosen] = useState(false);
  const [initialShowCopyFromMeeting, setInitialShowCopyFromMeeting] = useState(false);
  const [initialShowUpload, setInitialShowUpload] = useState(false);
  const [isImportingAttendees, setIsImportingAttendees] = useState(false);
  const [cloneModalInitialTab, setCloneModalInitialTab] = useState<CloneModalTab | undefined>(undefined);
  const [pendingOnBehalfOfName, setPendingOnBehalfOfName] = useState<string | null>(null);
  const [onBehalfOfName, setOnBehalfOfName] = useState<string | null>(null);
  const [coOrganizerName, setCoOrganizerName] = useState<string | null>(null);
  const [organizerName, setOrganizerName] = useState<string | null>(null);

  // ── Refresh state ────────────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());
  const [refreshedAgo, setRefreshedAgo] = useState('just now');

  // Pre-load leader EA config and user EA config cache on mount
  useEffect(() => {
    LeaderEAConfigService.preload().catch((err) => logger.warn('Failed to preload leader EA config', { error: err }));
    UserEAConfigService.preload(Alias).catch((err) => logger.warn('Failed to preload user EA config', { error: err }));
  }, []);

  useEffect(() => {
    const update = () => {
      const diffMinutes = Math.floor(DateTime.now().diff(DateTime.fromJSDate(lastRefreshedAt), 'minutes').minutes);
      if (diffMinutes < 1) {
        setRefreshedAgo('just now');
      } else if (diffMinutes < 60) {
        setRefreshedAgo(`${diffMinutes}m ago`);
      } else if (diffMinutes < 60 * 24) {
        setRefreshedAgo(`${Math.floor(diffMinutes / 60)}h ago`);
      } else {
        setRefreshedAgo(`${Math.floor(diffMinutes / (60 * 24))}d ago`);
      }
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [lastRefreshedAt]);

  const [pendingTemplateFile, setPendingTemplateFile] = useState<File | null>(null);
  const [creatorEmployee, setCreatorEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    logger.info('User authenticated successfully!', { app: 'EA Huddle' });
  }, []);

  // ── Core hooks ───────────────────────────────────────────────────────
  const { flashMessages, addFlashMessage, removeFlashMessage } = useFlashMessages();

  const {
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeUpdate,
    handleAttendeeRemove,
    handleAddProxy,
    handleEaOverride,
    handleEaRevert,
    getExistingEmails,
    findDuplicateEmails,
    replaceDocumentAttendees,
    appendAttendees,
    resortAttendees,
    setResolvedAttendees,
    sortBy,
    setSortBy,
    favoriteLoginNames,
    toggleFavorite: togglePersistedFavorite,
    refreshFavorites
  } = useAttendeeManagement(Alias);

  const { isProcessingDocument, documentError, handleFormattedContentExtracted, processDocument } = useDocumentProcessing();
  const {
    recentMeetings,
    allRecentMeetings,
    totalMeetingCount,
    currentPage,
    totalPages,
    goToPage,
    addRecentMeeting,
    updateMeetingSubject,
    deleteRecentMeeting,
    refreshMeetings
    // Pagination is now handled in the Sidebar based on maxVisible (viewport-aware)
    // Pass all meetings to the Sidebar and let it paginate visually
  } = useRecentMeetings(Alias, MEETING_CONSTANTS.MAX_RECENT_MEETINGS);

  // ── Shared meeting + access control ──────────────────────────────────
  const sharedMeeting = useSharedMeeting({
    alias: Alias,
    meetingId: searchParams.get('meetingId'),
    creator: searchParams.get('creator'),
    addFlashMessage,
    setMeetingFormData: (updater) => persistence.setMeetingFormData(updater),
    handleAddAttendee,
    appendAttendees,
    setInitialSnapshot: (snapshot) => persistence.setInitialSnapshot(snapshot),
    setSortBy
  });

  // Fetch creator employee details when access is denied
  useEffect(() => {
    if (!sharedMeeting.isAccessDenied || !sharedMeeting.sharedCreator) return;
    EmployeeService.getEmployeeByLogin(sharedMeeting.sharedCreator)
      .then(setCreatorEmployee)
      .catch(() => {});
  }, [sharedMeeting.isAccessDenied, sharedMeeting.sharedCreator]);

  // Load attachments from DDB when meeting loads
  useEffect(() => {
    if (!sharedMeeting.sharedMeetingId || !sharedMeeting.sharedCreator) return;
    EmployeeSettingsService.getMeeting(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId)
      .then((meeting) => {
        if (meeting?.attachments && meeting.attachments.length > 0) {
          setUploadedAttachments(
            meeting.attachments.map((s3Uri) => ({
              fileName: s3Uri.split('/').pop() ?? 'attachment',
              s3Uri
            }))
          );
        }
      })
      .catch(() => {});
  }, [sharedMeeting.sharedMeetingId, sharedMeeting.sharedCreator]);

  // ── Meeting persistence ──────────────────────────────────────────────
  const persistence = useMeetingPersistence({
    alias: Alias,
    creatorAlias: sharedMeeting.sharedCreator,
    sharedMeetingId: sharedMeeting.sharedMeetingId,
    isSharedCreator: sharedMeeting.isSharedCreator,
    resolvedAttendees,
    isLoadingSharedMeeting: sharedMeeting.isLoadingSharedMeeting,
    addFlashMessage,
    addRecentMeeting,
    updateRecentMeetingSubject: updateMeetingSubject,
    setSearchParams: (params, options) => setSearchParams(params, options),
    sortBy
  });

  // ── Scheduling ───────────────────────────────────────────────────────
  const scheduling = useScheduling({
    meetingId: sharedMeeting.sharedMeetingId,
    creatorAlias: sharedMeeting.sharedCreator,
    currentUserAlias: Alias,
    currentUserName: firstName || Alias,
    meetingSubject: persistence.meetingFormData.subject,
    coOrganizerLogin: persistence.meetingFormData.coOrganizerLogin
  });

  // ── Notifications ────────────────────────────────────────────────────
  // On homepage or as creator: show all notifications. As non-creator on shared link: scope to that meeting.
  const notificationMeetingFilter = sharedMeeting.sharedMeetingId && !scheduling.hasEditAccess ? sharedMeeting.sharedMeetingId : null;
  const notificationActions = useNotifications({
    currentUserAlias: Alias,
    meetingId: notificationMeetingFilter
  });

  const { configs: userEAConfigs, saveConfig: saveUserEAConfig, deleteConfig: deleteUserEAConfig } = useUserEAConfig({ userAlias: Alias });

  // ── EA Override change handler ─────────────────────────────────────
  // After a user EA override is added/edited/deleted, re-resolve EAs for the
  // currently open meeting's attendees so the change is reflected in the UI.
  // Manually-overridden attendees (per-meeting) are skipped — manual override
  // always wins. Updating selectedExecutiveAssistant naturally triggers the
  // attendee autosave, which persists the new eaLogin + authorizedLogins.
  const handleUserEAOverrideChanged = useCallback(async () => {
    if (!sharedMeeting.sharedMeetingId || resolvedAttendees.length === 0) return;
    try {
      const updates = await Promise.all(
        resolvedAttendees.map(async (a) => {
          if (a.isEaOverridden || !a.selectedEmployee) return null;
          const newEa = await EmployeeService.findExecutiveAssistant(a.selectedEmployee, Alias);
          const oldEa = a.selectedExecutiveAssistant;
          const oldLogin = oldEa?.login_name ?? null;
          const newLogin = newEa?.login_name ?? null;
          const oldEmail = oldEa?.employee_internal_email_address ?? null;
          const newEmail = newEa?.employee_internal_email_address ?? null;
          if (oldLogin === newLogin && oldEmail === newEmail) return null;
          return { id: a.id, ea: newEa };
        })
      );
      const changes = updates.filter((u): u is { id: string; ea: Employee | null } => u !== null);
      if (changes.length === 0) return;
      setResolvedAttendees((prev) =>
        prev.map((a) => {
          const change = changes.find((c) => c.id === a.id);
          return change ? { ...a, selectedExecutiveAssistant: change.ea ?? undefined } : a;
        })
      );
    } catch (error) {
      logger.warn('Failed to re-resolve EAs after override change', { error });
    }
  }, [sharedMeeting.sharedMeetingId, resolvedAttendees, Alias, setResolvedAttendees]);

  const saveUserEAConfigAndRefresh = useCallback(
    async (config: Parameters<typeof saveUserEAConfig>[0]) => {
      await saveUserEAConfig(config);
      await handleUserEAOverrideChanged();
    },
    [saveUserEAConfig, handleUserEAOverrideChanged]
  );

  const deleteUserEAConfigAndRefresh = useCallback(
    async (leaderAlias: string) => {
      await deleteUserEAConfig(leaderAlias);
      await handleUserEAOverrideChanged();
    },
    [deleteUserEAConfig, handleUserEAOverrideChanged]
  );

  // ── Refresh handler ──────────────────────────────────────────────────
  const unresolvedAttendees = useMemo(
    () => resolvedAttendees.filter((a) => a.status === 'notfound' || a.status === 'multiple' || !a.selectedEmployee),
    [resolvedAttendees]
  );

  // Warn on browser refresh/close if there are unresolved attendees
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unresolvedAttendees.length > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unresolvedAttendees.length]);

  const handleRefresh = useCallback(async () => {
    if (!sharedMeeting.sharedMeetingId || !sharedMeeting.sharedCreator) return;
    setIsRefreshing(true);
    try {
      // Refresh meeting metadata
      const meetingData = await EmployeeSettingsService.getMeeting(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId);
      if (meetingData) {
        persistence.setMeetingFormData((prev) => ({
          ...prev,
          ...(meetingData.subject ? { subject: meetingData.subject } : {}),
          ...(meetingData.agenda !== undefined ? { agenda: meetingData.agenda } : {}),
          ...(meetingData.duration ? { duration: meetingData.duration } : {}),
          ...(meetingData.repeat ? { repeat: meetingData.repeat } : {}),
          ...(meetingData.onBehalfOfLogin !== undefined ? { onBehalfOfLogin: meetingData.onBehalfOfLogin } : {})
        }));

        // Resolve on-behalf-of display name
        if (meetingData.onBehalfOfLogin) {
          EmployeeService.getEmployeeByLogin(meetingData.onBehalfOfLogin)
            .then((emp) => setOnBehalfOfName(emp?.employee_name ?? null))
            .catch(() => setOnBehalfOfName(null));
        } else {
          setOnBehalfOfName(null);
        }

        // Resolve co-organizer display name
        if (meetingData.coOrganizerLogin) {
          EmployeeService.getEmployeeByLogin(meetingData.coOrganizerLogin)
            .then((emp) => setCoOrganizerName(emp?.employee_name ?? null))
            .catch(() => setCoOrganizerName(null));
        } else {
          setCoOrganizerName(null);
        }

        // Refresh attendees — diff against current list
        if (meetingData.attendees) {
          const ddbAttendeeMap = new Map(meetingData.attendees.map((a) => [a.loginName.toLowerCase(), a]));
          const currentLogins = new Set(resolvedAttendees.map((a) => (a.selectedEmployee?.login_name || a.inputName).toLowerCase()));

          // Remove attendees no longer in DDB
          resolvedAttendees.forEach((a) => {
            const login = (a.selectedEmployee?.login_name || a.inputName).toLowerCase();
            if (!ddbAttendeeMap.has(login)) handleAttendeeRemove(a.id);
          });

          // Update priorities for existing attendees
          resolvedAttendees.forEach((a) => {
            const login = (a.selectedEmployee?.login_name || a.inputName).toLowerCase();
            const ddbAttendee = ddbAttendeeMap.get(login);
            if (ddbAttendee && ddbAttendee.priority !== a.priority) {
              handleAttendeeUpdate(a.id, { priority: ddbAttendee.priority });
            }
          });

          // Sync EA override state from DDB — collect promises and await before re-sorting
          const eaOverrideSyncPromises: Promise<void>[] = [];
          resolvedAttendees.forEach((currentAttendee) => {
            const login = (currentAttendee.selectedEmployee?.login_name || currentAttendee.inputName).toLowerCase();
            const ddbAttendee = ddbAttendeeMap.get(login);
            if (ddbAttendee && ddbAttendee.isEaOverridden && !currentAttendee.isEaOverridden && ddbAttendee.eaLogin) {
              eaOverrideSyncPromises.push(
                EmployeeService.getEmployeeByLogin(ddbAttendee.eaLogin)
                  .then((eaEmployee) => {
                    if (eaEmployee) handleEaOverride(currentAttendee.id, eaEmployee);
                  })
                  .catch(() => {
                    /* keep current EA on failure */
                  })
              );
            }
          });
          await Promise.all(eaOverrideSyncPromises);

          // Add attendees that are new in DDB
          const newAttendees = meetingData.attendees.filter((a) => !currentLogins.has(a.loginName.toLowerCase()));
          if (newAttendees.length > 0) {
            await loadAttendeesInBatches(newAttendees, handleAddAttendee);
          }

          // Re-sort after priority changes or new additions
          resortAttendees();
        }
        // Sync selected time slot from creator's latest data
        setSelectedSlotId(meetingData.selectedSlotId ?? null);

        // Sync attachments
        setUploadedAttachments(
          (meetingData.attachments ?? []).map((s3Uri) => ({
            fileName: s3Uri.split('/').pop() ?? 'attachment',
            s3Uri
          }))
        );
      }
      // Refresh scheduling data (time slots, votes, comments)
      await scheduling.loadSchedulingData();
      await notificationActions.refresh();
      await Promise.all([refreshFavorites(), refreshMeetings()]);
      setLastRefreshedAt(new Date());
    } catch (error) {
      logger.error('Failed to refresh meeting data', { error });
    } finally {
      setIsRefreshing(false);
    }
  }, [
    sharedMeeting.sharedMeetingId,
    sharedMeeting.sharedCreator,
    scheduling,
    persistence,
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeRemove,
    handleAttendeeUpdate,
    handleEaOverride,
    resortAttendees,
    notificationActions,
    refreshFavorites,
    refreshMeetings
  ]);

  const handleRefreshClick = useCallback(() => {
    if (unresolvedAttendees.length > 0) {
      setShowRefreshWarning(true);
    } else {
      handleRefresh();
    }
  }, [unresolvedAttendees.length, handleRefresh]);

  // Restore onBehalfOfLogin from loaded shared meeting data (legacy path)
  useEffect(() => {
    if (!sharedMeeting.loadedMeetingData) return;
    persistence.setMeetingFormData((prev) => ({
      ...prev,
      ...(sharedMeeting.loadedMeetingData!.onBehalfOfLogin ? { onBehalfOfLogin: sharedMeeting.loadedMeetingData!.onBehalfOfLogin } : {})
    }));
  }, [sharedMeeting.loadedMeetingData]);

  // Resolve onBehalfOf employee name whenever the login changes
  useEffect(() => {
    const login = persistence.meetingFormData.onBehalfOfLogin;
    if (!login) {
      setOnBehalfOfName(null);
      return;
    }
    let cancelled = false;
    EmployeeService.getEmployeeByLogin(login)
      .then((employee) => {
        if (!cancelled) setOnBehalfOfName(employee?.employee_name ?? null);
      })
      .catch(() => {
        if (!cancelled) setOnBehalfOfName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [persistence.meetingFormData.onBehalfOfLogin]);

  // Resolve co-organizer employee name whenever the login changes
  useEffect(() => {
    const login = persistence.meetingFormData.coOrganizerLogin;
    if (!login) {
      setCoOrganizerName(null);
      return;
    }
    let cancelled = false;
    EmployeeService.getEmployeeByLogin(login)
      .then((employee) => {
        if (!cancelled) setCoOrganizerName(employee?.employee_name ?? null);
      })
      .catch(() => {
        if (!cancelled) setCoOrganizerName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [persistence.meetingFormData.coOrganizerLogin]);

  // Resolve organizer employee name whenever the organizer login changes
  const effectiveOrganizerAlias = persistence.meetingFormData.organizerAlias || sharedMeeting.sharedCreator || Alias;
  useEffect(() => {
    if (effectiveOrganizerAlias === Alias) {
      setOrganizerName(fullName);
      return;
    }
    let cancelled = false;
    EmployeeService.getEmployeeByLogin(effectiveOrganizerAlias)
      .then((employee) => {
        if (!cancelled) setOrganizerName(employee?.employee_name ?? effectiveOrganizerAlias);
      })
      .catch(() => {
        if (!cancelled) setOrganizerName(effectiveOrganizerAlias);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveOrganizerAlias, Alias, fullName]);

  // Sync duration/repeat: scheduling data (DDB) takes precedence over shared meeting snapshot
  useEffect(() => {
    const source = scheduling.schedulingData ?? sharedMeeting.loadedMeetingData;
    if (source?.duration || source?.repeat) {
      persistence.setMeetingFormData((prev) => ({
        ...prev,
        ...(source?.duration ? { duration: source.duration } : {}),
        ...(source?.repeat ? { repeat: source.repeat } : {})
      }));
    }
  }, [scheduling.schedulingData, sharedMeeting.loadedMeetingData]);

  // ── Favorites ────────────────────────────────────────────────────────
  const existingLoginNames = React.useMemo(
    () => new Set(resolvedAttendees.map((a) => (a.isGroup ? a.inputName : a.selectedEmployee?.login_name)).filter(Boolean) as string[]),
    [resolvedAttendees]
  );

  const handleFavoriteClick = useCallback(
    async (loginName: string, priority: AttendeePriority = AttendeePriority.Optional) => {
      // Handle group favorites (prefixed with 'group:')
      if (loginName.startsWith('group:')) {
        const groupId = loginName.slice(6);
        if (existingLoginNames.has(groupId)) return;
        const members = await BrassService.listActorGroupMembership(groupId, BrassGroupType.AntGroup);
        appendAttendees([
          {
            id: `group-${uuidv4()}`,
            inputName: groupId,
            matches: [],
            selectedEmployee: null,
            selectedExecutiveAssistant: null,
            status: 'exact',
            priority,
            resolvedEmail: `${groupId}@amazon.com`,
            isGroup: true,
            groupType: BrassGroupType.AntGroup,
            groupMembers: members
          }
        ]);
        return;
      }

      const normalizedLogin = loginName.toLowerCase();
      if (existingLoginNames.has(normalizedLogin)) return;

      const employee: Employee = {
        login_name: normalizedLogin,
        employee_name: '',
        employee_internal_email_address: '',
        job_title_name: '',
        location_city_name: '',
        location_state_name: '',
        job_level_name: '',
        department_name_current: ''
      };
      handleAddAttendee(employee, priority);
    },
    [existingLoginNames, handleAddAttendee, appendAttendees]
  );

  const handleToggleFavorite = useCallback(
    (id: string) => {
      const attendee = resolvedAttendees.find((a) => a.id === id);
      if (attendee) {
        const loginName = attendee.isGroup ? `group:${attendee.inputName}` : attendee.selectedEmployee?.login_name;
        handleAttendeeUpdate(id, { isFavorite: !attendee.isFavorite });
        if (loginName) {
          togglePersistedFavorite(loginName);
        }
      }
    },
    [resolvedAttendees, handleAttendeeUpdate, togglePersistedFavorite]
  );

  // ── Derived values ───────────────────────────────────────────────────
  const existingEmails = getExistingEmails();
  const duplicateEmails = findDuplicateEmails();
  const executiveAssistants = MeetingService.collectExecutiveAssistants(resolvedAttendees);

  // ── Document handling ────────────────────────────────────────────────
  const handleFileSelected = useCallback(
    async (file: File) => {
      const result = await processDocument(file, eventsApiContext, Alias, persistence.manuallyEditedMeetingFormDataFields);
      if (!result.success) {
        // Open the upload modal so user can retry with the full interface
        setInitialShowUpload(true);
        return;
      }

      if (result.formUpdates) {
        persistence.setMeetingFormData((prev) => ({ ...prev, ...result.formUpdates }));
      }
      if (result.parsedAttendees) {
        replaceDocumentAttendees(result.parsedAttendees);
      }

      setShowUploadTemplateModal(false);
      setInitialShowUpload(false);
      setAttendeePathChosen(true);

      if (result.unresolvedOnBehalfOf) {
        setPendingOnBehalfOfName(result.unresolvedOnBehalfOf);
      }

      // Auto-register meeting if not yet registered (path picker flow)
      const meetingId =
        sharedMeeting.sharedMeetingId ??
        (await persistence.ensureMeetingCreated({
          subject: result.formUpdates?.subject ?? persistence.meetingFormData.subject,
          agenda: result.formUpdates?.agenda ?? persistence.meetingFormData.agenda,
          duration: result.formUpdates?.duration ?? (persistence.meetingFormData.duration || 30),
          repeat: result.formUpdates?.repeat ?? (persistence.meetingFormData.repeat || 'none'),
          onBehalfOfLogin: result.formUpdates?.onBehalfOfLogin ?? persistence.meetingFormData.onBehalfOfLogin
        }));

      // Upload template as meeting attachment
      if (meetingId) {
        const organizer = sharedMeeting.sharedCreator ?? Alias;
        const uploadResult = await EAHuddleService.uploadMeetingAttachment(file, organizer, meetingId);
        if (uploadResult.success && uploadResult.s3Uri) {
          await EmployeeSettingsService.appendAttachment(organizer, meetingId, uploadResult.s3Uri);
          setUploadedAttachments((prev) => [...prev, { fileName: file.name, s3Uri: uploadResult.s3Uri! }]);
          logger.info('Template uploaded as attachment', { meetingId, s3Uri: uploadResult.s3Uri });
        }
      }
    },
    [
      processDocument,
      eventsApiContext,
      Alias,
      persistence.manuallyEditedMeetingFormDataFields,
      replaceDocumentAttendees,
      persistence.setMeetingFormData,
      sharedMeeting
    ]
  );

  // Flush pending template file upload when meeting is created
  useEffect(() => {
    if (!sharedMeeting.sharedMeetingId || !pendingTemplateFile) return;
    const meetingId = sharedMeeting.sharedMeetingId;
    const organizer = sharedMeeting.sharedCreator ?? Alias;
    const file = pendingTemplateFile;
    setPendingTemplateFile(null);

    (async () => {
      const uploadResult = await EAHuddleService.uploadMeetingAttachment(file, organizer, meetingId);
      if (uploadResult.success && uploadResult.s3Uri) {
        await EmployeeSettingsService.appendAttachment(organizer, meetingId, uploadResult.s3Uri);
        setUploadedAttachments((prev) => [...prev, { fileName: file.name, s3Uri: uploadResult.s3Uri! }]);
        logger.info('Pending template uploaded as attachment', { meetingId, s3Uri: uploadResult.s3Uri });
      }
    })();
  }, [sharedMeeting.sharedMeetingId, pendingTemplateFile, sharedMeeting.sharedCreator, Alias]);

  // ── Duration / Repeat handlers (for MeetingFormContainer) ────────────
  const handleDurationChange = useCallback(
    (value: number) => {
      persistence.setMeetingFormData((prev) => ({ ...prev, duration: value }));
      if (sharedMeeting.sharedMeetingId) {
        scheduling.updateDuration(value);
      }
    },
    [sharedMeeting.sharedMeetingId, scheduling, persistence]
  );

  const handleRepeatChange = useCallback(
    (value: RepeatOption) => {
      persistence.setMeetingFormData((prev) => ({ ...prev, repeat: value }));
      if (sharedMeeting.sharedMeetingId) {
        scheduling.updateRepeat(value);
      }
    },
    [sharedMeeting.sharedMeetingId, scheduling, persistence]
  );

  // ── Simple callbacks ─────────────────────────────────────────────────
  const handleCreateMeeting = useCallback(
    async (selectedSlot?: TimeSlot) => {
      try {
        const outlookUrl = await MeetingService.generateOutlookWebUrl(
          persistence.meetingFormData,
          resolvedAttendees,
          selectedSlot,
          organizerName || fullName,
          effectiveOrganizerAlias,
          onBehalfOfName,
          coOrganizerName
        );
        window.open(outlookUrl, '_blank');
      } catch (error) {
        logger.error('Failed to generate Outlook URL', { error });
        addFlashMessage('error', 'Failed to open Outlook. Please try again.');
      }
    },
    [
      persistence.meetingFormData,
      resolvedAttendees,
      organizerName,
      fullName,
      effectiveOrganizerAlias,
      onBehalfOfName,
      coOrganizerName,
      addFlashMessage
    ]
  );

  const handleSendNoteToExecutiveAssistants = useCallback(async () => {
    try {
      const mailtoUrl = await MeetingService.generateEAMailtoUrl(
        persistence.meetingFormData,
        resolvedAttendees,
        scheduling.activeTimeSlots,
        sharedMeeting.sharedMeetingId,
        sharedMeeting.sharedCreator ?? Alias,
        undefined,
        organizerName || fullName,
        effectiveOrganizerAlias
      );
      window.open(mailtoUrl, '_blank');
    } catch (error) {
      addFlashMessage('error', (error as Error).message || 'Failed to send note to Executive Assistants.');
    }
  }, [
    persistence.meetingFormData,
    resolvedAttendees,
    addFlashMessage,
    scheduling.activeTimeSlots,
    sharedMeeting.sharedMeetingId,
    sharedMeeting.sharedCreator,
    Alias,
    organizerName,
    fullName,
    effectiveOrganizerAlias
  ]);

  const handleAddAttendeeWithTypes = useCallback(
    (employee: Employee, priority: AttendeePriority) => {
      handleAddAttendee(employee, priority);
    },
    [handleAddAttendee]
  );

  const handleMultiBadgerClick = () => {
    const logins = resolvedAttendees.map((a) => a.selectedEmployee?.login_name).filter(Boolean) as string[];
    window.open(generateMultiBadgerUrl(logins), '_blank');
  };

  const handleDownloadCsv = () => {
    exportAttendeeHierarchyToCsv(resolvedAttendees, (errorMessage) => {
      addFlashMessage('error', errorMessage);
    });
  };

  const handleAttendeePathSelect = useCallback(
    async (path: AttendeePath) => {
      if (path === 'upload') {
        setInitialShowUpload(true);
      } else if (path === 'clone') {
        setInitialShowCopyFromMeeting(true);
      } else {
        try {
          await persistence.ensureMeetingCreated({
            subject: persistence.meetingFormData.subject,
            agenda: persistence.meetingFormData.agenda,
            duration: persistence.meetingFormData.duration || 30,
            repeat: persistence.meetingFormData.repeat || 'none',
            onBehalfOfLogin: persistence.meetingFormData.onBehalfOfLogin
          });
          setAttendeePathChosen(true);
        } catch {
          addFlashMessage('error', 'Failed to create meeting. Please try again.');
        }
      }
    },
    [persistence, addFlashMessage]
  );

  // Determine if this is a shared link (has meetingId in URL)
  const isSharedLink = !!searchParams.get('meetingId');

  // ── Button-modal routing for ActionCard ──────────────────────────────
  const handleActionButtonClick = useCallback(
    (path: AttendeePath, buttonLabel: string) => {
      switch (path) {
        case 'upload':
          // Single "Upload" button opens the upload modal
          setInitialShowUpload(true);
          break;
        case 'clone':
          // Single "Clone Meeting" button opens the clone modal (defaults to past meetings tab)
          setInitialShowCopyFromMeeting(true);
          setCloneModalInitialTab(undefined);
          break;
        case 'manual':
          handleAttendeePathSelect('manual');
          break;
        default:
          handleAttendeePathSelect(path);
          break;
      }
    },
    [handleAttendeePathSelect]
  );

  const noop = useCallback(() => {}, []);
  const sharedFormProps = {
    organizerAlias: persistence.meetingFormData.organizerAlias || sharedMeeting.sharedCreator || Alias,
    subject: persistence.meetingFormData.subject,
    agenda: persistence.meetingFormData.agenda,
    duration: persistence.meetingFormData.duration,
    repeat: persistence.meetingFormData.repeat,
    onBehalfOfLogin: persistence.meetingFormData.onBehalfOfLogin,
    coOrganizerLogin: persistence.meetingFormData.coOrganizerLogin,
    isRegistered: !!sharedMeeting.sharedMeetingId,
    meetingId: sharedMeeting.sharedMeetingId,
    creatorAlias: sharedMeeting.sharedCreator ?? Alias,
    uploadedAttachments,
    meetingAttendees: buildMeetingAttendees(resolvedAttendees)
  };

  // ── Meeting form element (shared between home page and Create Meeting modal) ──
  const meetingFormElement = (
    <MeetingFormContainer
      {...sharedFormProps}
      onSubjectChange={(value) => persistence.handleInputChange('subject', value)}
      onAgendaChange={(value) => persistence.handleInputChange('agenda', value)}
      onDurationChange={handleDurationChange}
      onRepeatChange={handleRepeatChange}
      onBehalfOfChange={(login, currentCoOrganizerLogin, options) => {
        const previousLogin = persistence.meetingFormData.onBehalfOfLogin;
        persistence.setMeetingFormData((prev) => ({ ...prev, onBehalfOfLogin: login }));
        if (options?.skipPersist) return;
        if (sharedMeeting.sharedMeetingId && sharedMeeting.sharedCreator) {
          const attendees = buildMeetingAttendees(resolvedAttendees);
          const coOrg = currentCoOrganizerLogin !== undefined ? currentCoOrganizerLogin : persistence.meetingFormData.coOrganizerLogin;
          EmployeeSettingsService.updateMeetingMetadata(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId, {
            onBehalfOfLogin: login,
            authorizedLogins: computeAuthorizedLogins(sharedMeeting.sharedCreator, attendees, {
              organizerLogin: sharedMeeting.sharedCreator,
              onBehalfOfLogin: login,
              coOrganizerLogin: coOrg
            })
          }).catch((error) => {
            logger.error('Failed to persist onBehalfOf change', { error, meetingId: sharedMeeting.sharedMeetingId });
            persistence.setMeetingFormData((prev) => ({ ...prev, onBehalfOfLogin: previousLogin }));
          });
        }
      }}
      onCoOrganizerChange={(login, currentBehalfOfLogin, options) => {
        const previousLogin = persistence.meetingFormData.coOrganizerLogin;
        persistence.setMeetingFormData((prev) => ({ ...prev, coOrganizerLogin: login }));
        if (options?.skipPersist) return;
        if (sharedMeeting.sharedMeetingId && sharedMeeting.sharedCreator) {
          const attendees = buildMeetingAttendees(resolvedAttendees);
          const behalfOf = currentBehalfOfLogin !== undefined ? currentBehalfOfLogin : persistence.meetingFormData.onBehalfOfLogin;
          EmployeeSettingsService.updateMeetingMetadata(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId, {
            coOrganizerLogin: login,
            authorizedLogins: computeAuthorizedLogins(sharedMeeting.sharedCreator, attendees, {
              organizerLogin: sharedMeeting.sharedCreator,
              onBehalfOfLogin: behalfOf,
              coOrganizerLogin: login
            })
          }).catch((error) => {
            logger.error('Failed to persist co-organizer change', { error, meetingId: sharedMeeting.sharedMeetingId });
            persistence.setMeetingFormData((prev) => ({ ...prev, coOrganizerLogin: previousLogin }));
          });
        }
      }}
      onRegisterMeeting={(metadata) => persistence.handleCreateSharedLink(metadata)}
      isSaving={persistence.isSavingMeeting}
      isReadOnly={sharedMeeting.isSharedViewer}
      defaultEditing={attendeePathChosen}
      onUploadedAttachmentsChange={setUploadedAttachments}
      pendingOnBehalfOfName={pendingOnBehalfOfName}
      onPendingOnBehalfOfDismiss={() => setPendingOnBehalfOfName(null)}
    />
  );

  const meetingFormReadOnly = (
    <MeetingFormContainer
      {...sharedFormProps}
      onSubjectChange={noop}
      onAgendaChange={noop}
      onDurationChange={noop}
      onRepeatChange={noop}
      onBehalfOfChange={noop}
      onRegisterMeeting={noop}
      isReadOnly={true}
      onUploadedAttachmentsChange={noop}
      pendingOnBehalfOfName={pendingOnBehalfOfName}
      onPendingOnBehalfOfDismiss={() => setPendingOnBehalfOfName(null)}
    />
  );

  // ── Main layout ──────────────────────────────────────────────────────
  return (
    <div className="ea-huddle-page">
      <AppLayout
        toolsHide={true}
        navigation={
          <Sidebar
            favoriteLoginNames={favoriteLoginNames}
            existingLoginNames={existingLoginNames}
            onFavoriteClick={handleFavoriteClick}
            recentMeetings={recentMeetings}
            totalMeetingCount={totalMeetingCount}
            onDeleteMeeting={deleteRecentMeeting}
            isReadOnly={sharedMeeting.isSharedViewer}
            isMeetingRegistered={!!sharedMeeting.sharedMeetingId}
            notificationActions={notificationActions}
            isRefreshing={isRefreshing}
            onRefresh={sharedMeeting.sharedMeetingId && !sharedMeeting.isAccessDenied ? handleRefreshClick : undefined}
            userEAConfigs={userEAConfigs}
            onSaveUserEAConfig={saveUserEAConfigAndRefresh}
            onDeleteUserEAConfig={deleteUserEAConfigAndRefresh}
            onNewMeeting={
              attendeePathChosen || isSharedLink
                ? () => {
                    setAttendeePathChosen(false);
                    setSearchParams({}, { replace: true });
                  }
                : undefined
            }
          />
        }
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        notifications={
          flashMessages.length > 0 ? (
            <Flashbar
              items={flashMessages.map((message) => ({
                type: message.type,
                content: message.content,
                dismissible: true,
                onDismiss: () => removeFlashMessage(message.id),
                id: message.id
              }))}
            />
          ) : undefined
        }
        contentType="table"
        maxContentWidth={Number.MAX_VALUE}
        content={
          <>
            {showOutlookAlert && (
              <Alert
                type="warning"
                dismissible
                header="Outlook URL Change — Action Required"
                action={
                  <Button variant="normal" href="https://outlook.cloud.microsoft/mail/" target="_blank" iconAlign="right" iconName="external">
                    Open New Outlook
                  </Button>
                }
                onDismiss={() => {
                  setShowOutlookAlert(false);
                  localStorage.setItem('ea-huddle-outlook-alert-dismissed', 'true');
                }}
              >
                Microsoft is migrating Outlook from outlook.office365.com to outlook.cloud.microsoft. This change is being rolled out gradually to all
                users. If Huddle+ is not loading your data correctly, please log in to the new URL first, then return to Huddle+.
              </Alert>
            )}
            {sharedMeeting.isAccessDenied ? (
              <Box textAlign="center" padding={{ top: 'xxxl', bottom: 'xxxl' }}>
                <SpaceBetween size="m" alignItems="center">
                  <Box variant="h2">Access Denied</Box>
                  <Box variant="p" color="text-body-secondary">
                    You don't have access to this meeting. This can happen if your EA assignment changed recently or if you are not an attendee.
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    Please ask the meeting creator to refresh their meeting page to update access, or request to be added as an attendee.
                  </Box>
                  {sharedMeeting.sharedCreator && (
                    <Box>
                      <Box variant="p" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                        Meeting creator:
                      </Box>
                      {creatorEmployee ? <HyperBadge employee={creatorEmployee} /> : <Spinner size="normal" />}
                    </Box>
                  )}
                </SpaceBetween>
              </Box>
            ) : sharedMeeting.isLoadingSharedMeeting ? (
              <Box textAlign="center" padding={{ top: 'xxxl', bottom: 'xxxl' }}>
                <SpaceBetween size="s" alignItems="center">
                  <Spinner size="large" />
                  <Box fontSize="body-m" color="text-body-secondary">
                    {sharedMeeting.sharedMeetingProgress || 'Loading meeting data...'}
                  </Box>
                </SpaceBetween>
              </Box>
            ) : (
              <>
                {!attendeePathChosen && !sharedMeeting.isSharedViewer && !isSharedLink && (
                  <div className="meeting-content-grid">
                    <AttendeePathPicker
                      onSelect={handleAttendeePathSelect}
                      onButtonClick={handleActionButtonClick}
                      displayName={firstName}
                      disabledPaths={isProcessingDocument ? new Set<AttendeePath>(['clone', 'manual']) : undefined}
                      onFileDrop={handleFileSelected}
                      isProcessing={isProcessingDocument}
                      processingError={documentError}
                    />
                  </div>
                )}
                {(attendeePathChosen || sharedMeeting.isSharedViewer || isSharedLink) && meetingFormElement}
                <div className="meeting-content-grid">
                  {!!sharedMeeting.sharedMeetingId && (attendeePathChosen || sharedMeeting.isSharedViewer || isSharedLink) && (
                    <>
                      {isImportingAttendees ? (
                        <Box padding={{ vertical: 'l' }}>
                          <SpaceBetween size="s" direction="vertical" alignItems="center">
                            <Spinner size="large" />
                            <Box fontSize="body-m" color="text-body-secondary">
                              Loading attendees from meeting...
                            </Box>
                          </SpaceBetween>
                        </Box>
                      ) : (
                        <AddAttendeesContainer
                          initialExpanded={true}
                          formData={persistence.meetingFormData}
                          onInputChange={persistence.handleInputChange}
                          initialShowCopyFromMeeting={initialShowCopyFromMeeting}
                          initialShowUpload={initialShowUpload}
                          attendees={{
                            resolvedAttendees,
                            existingEmails,
                            duplicateEmails,
                            onAddAttendee: handleAddAttendeeWithTypes,
                            onAttendeeUpdate: handleAttendeeUpdate,
                            onAttendeeRemove: handleAttendeeRemove,
                            onToggleFavorite: handleToggleFavorite,
                            onAddProxy: handleAddProxy,
                            onResort: resortAttendees,
                            onEaOverride: handleEaOverride,
                            onEaRevert: handleEaRevert,
                            appendAttendees,
                            sortBy,
                            onSortByChange: setSortBy
                          }}
                          document={{
                            isProcessingDocument,
                            documentError,
                            onFileSelected: handleFileSelected,
                            onFormattedContentExtracted: handleFormattedContentExtracted,
                            showUploadModal: showUploadTemplateModal,
                            onHideUploadModal: () => setShowUploadTemplateModal(false)
                          }}
                          sharedMeeting={{
                            isLoadingSharedMeeting: sharedMeeting.isLoadingSharedMeeting,
                            sharedMeetingProgress: sharedMeeting.sharedMeetingProgress,
                            isReadOnly: sharedMeeting.isSharedViewer
                          }}
                          executiveAssistants={executiveAssistants}
                          onMultiBadgerClick={handleMultiBadgerClick}
                          onDownloadCsv={handleDownloadCsv}
                          recentMeetings={recentMeetings}
                        />
                      )}
                      <SchedulingContainer
                        initialExpanded={true}
                        organizerLogin={sharedMeeting.sharedCreator ?? Alias}
                        currentUserAlias={Alias}
                        currentUserName={Alias}
                        scheduling={{
                          ...scheduling,
                          hasEditAccess: sharedMeeting.sharedMeetingId ? scheduling.hasEditAccess : true
                        }}
                        onEnsureMeetingCreated={persistence.ensureMeetingCreated}
                        isReadOnly={sharedMeeting.isSharedViewer}
                        executiveAssistants={executiveAssistants}
                        onSendNoteToEAs={handleSendNoteToExecutiveAssistants}
                        meetingId={sharedMeeting.sharedMeetingId}
                        onCreateMeeting={handleCreateMeeting}
                        resolvedAttendeesCount={resolvedAttendees.length}
                        meetingSubject={persistence.meetingFormData.subject}
                        meetingDuration={persistence.meetingFormData.duration}
                        onBehalfOfLogin={persistence.meetingFormData.onBehalfOfLogin}
                        resolvedAttendees={resolvedAttendees}
                        meetingFormContent={meetingFormElement}
                        meetingFormReadOnly={meetingFormReadOnly}
                        onSelectedSlotChange={setSelectedSlotId}
                        externalSelectedSlotId={selectedSlotId}
                        onProposedSlotChange={setProposedSlot}
                        onHoveredSlotChange={setHoveredSlotId}
                        populateSlot={populateSlot}
                        directAddSlot={directAddSlot}
                      />
                      <TimezoneConverterContainer
                        resolvedAttendees={resolvedAttendees}
                        duration={persistence.meetingFormData.duration}
                        activeTimeSlots={scheduling.activeTimeSlots}
                        selectedSlotId={selectedSlotId}
                        proposedSlot={sharedMeeting.isSharedViewer ? null : proposedSlot}
                        hoveredSlotId={hoveredSlotId}
                        onTimeWindowClick={setPopulateSlot}
                        onTimeWindowDoubleClick={setDirectAddSlot}
                        isReadOnly={sharedMeeting.isSharedViewer}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </>
        }
      />

      {initialShowUpload && (
        <UploadTemplateContainer
          visible={initialShowUpload}
          onClose={() => setInitialShowUpload(false)}
          isProcessingDocument={isProcessingDocument}
          documentError={documentError}
          onFileSelected={handleFileSelected}
          onFormattedContentExtracted={handleFormattedContentExtracted}
        />
      )}

      {showRefreshWarning && (
        <Modal
          visible={true}
          onDismiss={() => setShowRefreshWarning(false)}
          header="Unresolved Attendees"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowRefreshWarning(false)}>
                  Cancel
                </Button>
                <span className="blue-button">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowRefreshWarning(false);
                      handleRefresh();
                    }}
                  >
                    Refresh Anyway
                  </Button>
                </span>
              </SpaceBetween>
            </Box>
          }
        >
          <Alert type="warning" statusIconAriaLabel="Warning">
            You have {unresolvedAttendees.length} unresolved attendee{unresolvedAttendees.length !== 1 ? 's' : ''} that will be lost on refresh
            because they haven't been saved to the meeting. Resolve or remove them first, or continue to refresh anyway.
          </Alert>
        </Modal>
      )}
      <ImportFromMeetingModal
        visible={initialShowCopyFromMeeting}
        onDismiss={() => {
          setInitialShowCopyFromMeeting(false);
          setCloneModalInitialTab(undefined);
        }}
        recentMeetings={allRecentMeetings}
        resolvedAttendees={resolvedAttendees}
        initialTab={cloneModalInitialTab}
        onImportComplete={async (attendees) => {
          setInitialShowCopyFromMeeting(false);
          setIsImportingAttendees(true);
          try {
            if (!sharedMeeting.sharedMeetingId) {
              await persistence.ensureMeetingCreated({
                subject: persistence.meetingFormData.subject,
                agenda: persistence.meetingFormData.agenda,
                duration: persistence.meetingFormData.duration || 30,
                repeat: persistence.meetingFormData.repeat || 'none',
                onBehalfOfLogin: persistence.meetingFormData.onBehalfOfLogin
              });
            }
            if (attendees.length > 0) {
              await loadAttendeesInBatches(attendees, handleAddAttendee);
            }
            setAttendeePathChosen(true);
          } catch {
            addFlashMessage('error', 'Failed to import attendees. Please try again.');
          } finally {
            setIsImportingAttendees(false);
          }
        }}
      />
    </div>
  );
};
